import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { BulkImportSchedulingView, BulkImportSchedulingViewProps } from './BulkImportScheduling.view';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { isEqual, parse } from 'date-fns';
import { DateStringFormat } from '../../../constants/time-formats';
import { useWatch } from 'react-hook-form';
import { FormElementAction } from '../../../models/basic-objects';

export type BulkImportSchedulingControllerProps = {
  // extend your BulkImportSchedulingViewProps
  formInstance: BulkImportSchedulingViewProps['formInstance'];
};

export const BulkImportSchedulingController = (props: BulkImportSchedulingControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { state, dispatch } = useContext(BulkActionsContext);
  const { formInstance } = props;
  const { getValues: getFormValues, setValue: setFormValue } = formInstance;

  const { scheduling } = state;
  const { checkboxes, content } = scheduling;
  const { evergreenRecycle } = content;

  const { expiryDate, expiryMaxRepeats } = evergreenRecycle;

  const watchExpireCountValue: number | undefined = useWatch({
    control: formInstance.control,
    name: 'expireAfterCount',
    defaultValue: expiryMaxRepeats ?? undefined
  });

  const checkboxesState = Object.values(checkboxes).reduce((prev, current) => {
    if (prev) return prev;
    return current;
  });

  const stableExpiryDate = useMemo(
    () => (expiryDate ? parse(expiryDate, DateStringFormat, new Date()) : null),
    [expiryDate]
  );

  const setExpireAfterDateHandler: FormElementAction<Date> = useCallback(
    (value, complete) => {
      complete();
      const dateStr = value.toLocaleDateString('en-US').split('/');
      const formattedDate = `${dateStr[2]}-${dateStr[0].padStart(2, '0')}-${dateStr[1].padStart(2, '0')}`;
      dispatch({ type: 'setExpiryDate', payload: formattedDate });
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch({ type: 'setChanges', payload: { section: 'scheduling', value: checkboxesState } });
  }, [checkboxesState, dispatch]);

  useEffect(() => {
    if (stableExpiryDate) {
      const currentFormValue = getFormValues('expireAfterDate');
      if (!isEqual(currentFormValue, stableExpiryDate)) {
        setFormValue('expireAfterDate', stableExpiryDate);
      }
    }
  }, [stableExpiryDate, setFormValue, getFormValues]);

  useEffect(() => {
    if (watchExpireCountValue && (typeof expiryMaxRepeats !== 'number' || expiryMaxRepeats !== watchExpireCountValue)) {
      dispatch({
        type: 'setExpiryMaxRepeats',
        payload: watchExpireCountValue
      });
    }
  }, [watchExpireCountValue, dispatch, expiryMaxRepeats]);

  return (
    <BulkImportSchedulingView
      formInstance={formInstance}
      setExpireAfterDateHandler={setExpireAfterDateHandler}
      expiryDate={stableExpiryDate}
    />
  );
};
